import { ScrollArea } from '@base-ui/react/scroll-area'
import { Select as BaseSelect } from '@base-ui/react/select'
import clsx from 'clsx'
import { useEffect, useMemo, useRef } from 'react'

const DEFAULT_AVATAR_URL = 'https://storage.googleapis.com/cartwheel-assets/default-user-avatar.png'

export interface PeoplePickerPerson {
  id: string
  name: string
  avatarUrl?: string | null
  annotation?: string
  dividerAfter?: boolean
}

export interface PeoplePickerProps {
  people: PeoplePickerPerson[]
  value?: string | null
  onChange?: (personId: string | null, person?: PeoplePickerPerson) => void
  clearable?: boolean
  placeholder?: string
  loading?: boolean
  error?: boolean
  loadingMessage?: string
  errorMessage?: string
  emptyMessage?: string
  disabled?: boolean
  className?: string
  onOpenChange?: (open: boolean) => void
}

export function PeoplePicker({
  people,
  value,
  onChange,
  clearable = false,
  placeholder = 'Unassigned',
  loading = false,
  error = false,
  loadingMessage = 'Loading...',
  errorMessage = 'Unable to load people',
  emptyMessage = 'No people found',
  disabled = false,
  className,
  onOpenChange,
}: PeoplePickerProps) {
  const selectedValue = value ?? null
  const selectedPerson = people.find((person) => person.id === selectedValue) ?? null
  const selectItems = useMemo(
    () => people.map((person) => ({ value: person.id, label: person.name })),
    [people]
  )
  const lastEmittedValueRef = useRef<string | null>(selectedValue)

  useEffect(() => {
    lastEmittedValueRef.current = selectedValue
  }, [selectedValue])

  const emitChange = (nextValue: string | null) => {
    if (lastEmittedValueRef.current === nextValue) return

    lastEmittedValueRef.current = nextValue
    const nextPerson = people.find((person) => person.id === nextValue)
    if (nextValue === null) {
      onChange?.(null)
    } else {
      onChange?.(nextValue, nextPerson)
    }
  }

  return (
    <BaseSelect.Root
      items={selectItems}
      value={selectedValue}
      onValueChange={emitChange}
      disabled={disabled}
      onOpenChange={onOpenChange}
    >
      <div className={clsx('relative w-52', className)}>
        <BaseSelect.Trigger
          className={clsx(
            'flex h-10 w-full items-center rounded-lg bg-transparent px-2 text-left text-sm leading-[1.5] text-charcoal focus:outline-none focus:ring-2 focus:ring-forest/40 data-[popup-open]:bg-sand-25 data-disabled:cursor-not-allowed data-disabled:opacity-60',
            selectedPerson && 'pr-9'
          )}
        >
          <BaseSelect.Value>
            <PeoplePickerValue person={selectedPerson} placeholder={placeholder} />
          </BaseSelect.Value>
        </BaseSelect.Trigger>
        {clearable && selectedPerson && !disabled && (
          <button
            type="button"
            aria-label="Clear selected person"
            className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-charcoal-50 hover:bg-sand hover:text-charcoal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest"
            onMouseDown={(event) => {
              event.preventDefault()
              event.stopPropagation()
            }}
            onClick={(event) => {
              event.stopPropagation()
              emitChange(null)
            }}
          >
            <i className="fa-solid fa-xmark text-xs" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Base UI handles positioning; app-level z-index keeps this above drawer/card content. */}
      <BaseSelect.Positioner
        align="start"
        sideOffset={8}
        alignItemWithTrigger={false}
        className="z-50 outline-none select-none"
      >
        <BaseSelect.Popup className="box-border rounded-lg border border-gray-stroke bg-white shadow-sm w-[var(--anchor-width)] origin-[var(--transform-origin)]">
          <PeoplePickerOptions
            people={people}
            loading={loading}
            error={error}
            loadingMessage={loadingMessage}
            errorMessage={errorMessage}
            emptyMessage={emptyMessage}
          />
        </BaseSelect.Popup>
      </BaseSelect.Positioner>
    </BaseSelect.Root>
  )
}

function PeoplePickerValue({
  person,
  placeholder,
}: {
  person: PeoplePickerPerson | null
  placeholder: string
}) {
  if (!person) {
    return <span className="truncate text-charcoal-50">{placeholder}</span>
  }

  return (
    <span className="flex min-w-0 items-center gap-2">
      <PersonAvatar person={person} />
      <PersonName person={person} />
    </span>
  )
}

function PeoplePickerOptions({
  people,
  loading,
  error,
  loadingMessage,
  errorMessage,
  emptyMessage,
}: {
  people: PeoplePickerPerson[]
  loading: boolean
  error: boolean
  loadingMessage: string
  errorMessage: string
  emptyMessage: string
}) {
  if (loading) {
    return (
      <output aria-label={loadingMessage} className="flex flex-col gap-4 py-2">
        <PeoplePickerSkeleton />
        <PeoplePickerSkeleton />
        <PeoplePickerSkeleton />
        <PeoplePickerSkeleton />
      </output>
    )
  }

  if (error) {
    return (
      <div className="p-2 h-[var(--anchor-height)] text-sm leading-[1.5] text-brick-light">
        {errorMessage}
      </div>
    )
  }

  if (people.length === 0) {
    return (
      <div className="p-2 h-[var(--anchor-height)] text-sm leading-[1.5] text-charcoal-50">
        {emptyMessage}
      </div>
    )
  }

  const listHeight = Math.min(people.length * 48, 224)

  return (
    <ScrollArea.Root className="relative" style={{ height: listHeight }}>
      <ScrollArea.Viewport className="h-full overscroll-contain">
        <ScrollArea.Content className="flex flex-col">
          {people.map((person) => (
            <PeoplePickerOption key={person.id} person={person} />
          ))}
        </ScrollArea.Content>
      </ScrollArea.Viewport>
      <ScrollArea.Scrollbar
        orientation="vertical"
        className="right-3 flex w-2 justify-center rounded-full bg-transparent opacity-0 transition-opacity duration-150 data-[hovering]:opacity-100 data-[scrolling]:opacity-100"
      >
        <ScrollArea.Thumb className="w-1.5 rounded-full bg-charcoal-50/40 hover:bg-charcoal-50/60" />
      </ScrollArea.Scrollbar>
    </ScrollArea.Root>
  )
}

function PeoplePickerOption({ person }: { person: PeoplePickerPerson }) {
  return (
    <>
      <BaseSelect.Item
        value={person.id}
        className="flex p-2 w-full cursor-default items-center gap-2 text-left text-sm leading-[1.5] text-charcoal outline-none first:rounded-t-md last:rounded-b-md data-highlighted:bg-sand-25 data-selected:text-charcoal"
        render={(itemProps) => (
          <div {...itemProps}>
            <PersonAvatar person={person} />
            <BaseSelect.ItemText className="min-w-0 truncate">
              <PersonName person={person} />
            </BaseSelect.ItemText>
          </div>
        )}
      />
      {person.dividerAfter && <div className="h-px bg-gray-stroke" />}
    </>
  )
}

function PeoplePickerSkeleton() {
  return (
    <div className="flex p-2 h-8 items-center gap-2">
      <div className="size-8 shrink-0 animate-pulse rounded-full bg-sand" />
      <div className="h-3 w-24 animate-pulse rounded-full bg-sand" />
    </div>
  )
}

function PersonName({ person }: { person: PeoplePickerPerson }) {
  return (
    <span className="min-w-0 truncate text-charcoal">
      {person.name}
      {person.annotation && <span className="text-charcoal-50"> ({person.annotation})</span>}
    </span>
  )
}

function PersonAvatar({ person }: { person: PeoplePickerPerson }) {
  return (
    <img
      src={person.avatarUrl || DEFAULT_AVATAR_URL}
      alt=""
      aria-hidden="true"
      className="size-8 shrink-0 rounded-full"
    />
  )
}
